/**
 * Browser-side mirror of the runtime's streaming chat loop.
 *
 * Posts ``POST /v1/agents/{agentId}/responses`` with ``stream:true`` and
 * consumes the OpenAI Responses SSE protocol on the same response body.
 *
 * The shapes we care about:
 *
 *  - ``response.created``        — new bubble starts
 *  - ``response.output_text.delta`` — append to bubble text
 *  - ``response.output_text.done``  — final chunk of a text segment
 *  - ``response.output_item.added`` — non-text output (tool call) appears
 *  - ``response.output_item.done``  — tool call finished (status, output)
 *  - ``response.completed``      — terminal success, includes usage
 *  - ``response.failed``         — terminal failure, includes error
 *
 * After ``response.completed``/``response.failed`` the runtime writes a
 * literal ``data: [DONE]`` frame; we treat it as a stream-end marker.
 */
import { apiUrl, authHeaders } from "./api";

let noteCounter = 0;
function noteId(): string {
  noteCounter += 1;
  return `note-${noteCounter}-${Date.now().toString(36)}`;
}

export type BubbleKind = "user" | "agent";

export interface ChatImage {
  url: string;
  alt?: string;
  title?: string;
  source?: "user" | "assistant" | "tool" | "artifact";
}

export interface ToolCallEntry {
  id: string;
  toolId: string;
  status: "requested" | "running" | "succeeded" | "failed" | "denied" | "timeout";
  arguments?: Record<string, unknown>;
  output?: unknown;
  error?: string;
  startedAt?: number;
  durationMs?: number;
}

export interface BubbleNote {
  id: string;
  text: string;
  kind: "info" | "error";
}

export interface BubbleEvent {
  text: string;
  /**
   * Model chain-of-thought streamed on the dedicated reasoning channel
   * (DeepSeek `reasoning_content`, Anthropic extended-thinking blocks,
   * OpenAI o-series reasoning summaries). Rendered as a collapsible
   * "Thinking" panel above the assistant's user-visible text.
   */
  thinking: string;
  /** True while the reasoning channel is still streaming this turn. */
  thinkingActive: boolean;
  images: ChatImage[];
  events: BubbleNote[];
  toolCalls: ToolCallEntry[];
  errored: boolean;
  reaction?: { emoji: string; label: string };
  usage?: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    costUsd?: number;
  };
  elapsedMs?: number;
  responseId?: string;
}

export function newAgentBubble(): BubbleEvent {
  return {
    text: "",
    thinking: "",
    thinkingActive: false,
    images: [],
    events: [],
    toolCalls: [],
    errored: false,
  };
}

export interface RunResult {
  terminated: boolean;
  errored: boolean;
  terminalType?: string;
  responseId?: string;
}

export type ChatPart =
  | { type: "text"; text: string }
  | { type: "image_url"; url: string }
  | { type: "file_ref"; path: string };

interface SseFrame {
  /** Named ``event:`` field, or ``"message"`` for unnamed frames. */
  event: string;
  /** JSON payload from the ``data:`` field, or null if unparsable. */
  data: Record<string, unknown> | null;
  /** Raw data text, useful for the ``[DONE]`` sentinel. */
  raw: string;
}

const TERMINAL_TYPES = new Set(["response.completed", "response.failed"]);
export const ERROR_TERMINAL_EVENTS: ReadonlySet<string> = new Set(["response.failed"]);

/** Run one chat turn against the gateway's Responses endpoint. */
export async function runTurn(opts: {
  agentId: string;
  sessionId: string;
  input: string;
  extraParts?: ChatPart[];
  onUpdate: (patch: (prev: BubbleEvent) => BubbleEvent) => void;
  signal?: AbortSignal;
}): Promise<RunResult> {
  const t0 = Date.now();
  const inputItems = buildInputItems(opts.input, opts.extraParts ?? []);

  const resp = await fetch(apiUrl(`/agents/${encodeURIComponent(opts.agentId)}/responses`), {
    method: "POST",
    // ``credentials: "include"`` is required so the ``kos_session`` cookie
    // travels with the SSE request when the SPA + gateway are on
    // different origins. Without it the browser silently drops the
    // cookie and the gateway 401s the run.
    credentials: "include",
    headers: {
      "content-type": "application/json",
      accept: "text/event-stream",
      ...authHeaders(),
    },
    body: JSON.stringify({
      input: inputItems,
      session_id: opts.sessionId,
      // Dedup key: every turn in the same chat tab carries the same
      // sessionId; the runtime's `SessionRepository.get_or_create` uses
      // it to coalesce onto a single AgentSession row instead of
      // spawning one row per turn (which used to flood the rail).
      external_thread_key: opts.sessionId,
      stream: true,
    }),
    signal: opts.signal,
  });

  if (!resp.ok || !resp.body) {
    const body = await resp.text().catch(() => "");
    const msg = `POST /agents/${opts.agentId}/responses ${resp.status}: ${body.slice(0, 200)}`;
    opts.onUpdate((b) => ({
      ...b,
      events: [...b.events, { id: noteId(), text: `[gateway error] ${msg}`, kind: "error" }],
      errored: true,
    }));
    return { terminated: true, errored: true, terminalType: "gateway.error" };
  }

  const result = await consumeResponsesStream(resp.body, (frame) => {
    applyFrame(frame, opts.onUpdate);
  });

  if (result.terminated) {
    const elapsedMs = Date.now() - t0;
    opts.onUpdate((b) => ({ ...b, elapsedMs }));
  }
  return result;
}

function buildInputItems(
  text: string,
  extras: ChatPart[],
): string | Array<{ role: "user"; content: Array<Record<string, unknown>> }> {
  // The runtime accepts both a plain string and the OpenAI Responses input
  // shape. Plain text is the common case; extras (images, file refs) bump
  // us into the structured form.
  if (extras.length === 0) return text;

  const content: Array<Record<string, unknown>> = [];
  if (text.trim().length > 0) content.push({ type: "input_text", text });
  for (const part of extras) {
    if (part.type === "text") content.push({ type: "input_text", text: part.text });
    else if (part.type === "image_url") content.push({ type: "input_image", image_url: part.url });
    else if (part.type === "file_ref") content.push({ type: "input_file", file_path: part.path });
  }
  return [{ role: "user", content }];
}

async function consumeResponsesStream(
  body: ReadableStream<Uint8Array>,
  onFrame: (frame: SseFrame) => void,
): Promise<RunResult> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let terminated = false;
  let errored = false;
  let terminalType: string | undefined;
  let responseId: string | undefined;

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });

    let idx = buf.indexOf("\n\n");
    while (idx !== -1) {
      const block = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      idx = buf.indexOf("\n\n");

      const frame = parseSseFrame(block);
      if (!frame) continue;

      // The OpenAI sentinel ``data: [DONE]`` ends the stream.
      if (frame.raw.trim() === "[DONE]") {
        terminated = true;
        continue;
      }

      onFrame(frame);

      if (TERMINAL_TYPES.has(frame.event)) {
        terminated = true;
        terminalType = frame.event;
        if (frame.event === "response.failed") errored = true;
        const r = (frame.data?.response as Record<string, unknown> | undefined) ?? undefined;
        if (typeof r?.id === "string") responseId = r.id;
      }
    }
  }

  return { terminated, errored, terminalType, responseId };
}

function parseSseFrame(block: string): SseFrame | null {
  const lines = block.split("\n");
  let event = "message";
  const dataParts: string[] = [];
  for (const line of lines) {
    if (line.startsWith("event:")) event = line.slice(6).trim() || event;
    else if (line.startsWith("data:")) dataParts.push(line.slice(5).trimStart());
  }
  if (dataParts.length === 0) return null;
  const raw = dataParts.join("\n");
  let data: Record<string, unknown> | null = null;
  try {
    data = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    data = null;
  }
  return { event, data, raw };
}

function applyFrame(
  frame: SseFrame,
  onUpdate: (patch: (prev: BubbleEvent) => BubbleEvent) => void,
): void {
  const data = frame.data ?? {};
  const t = (data.type as string | undefined) ?? frame.event;

  switch (t) {
    case "response.created":
    case "response.in_progress": {
      const r = data.response as Record<string, unknown> | undefined;
      const id = typeof r?.id === "string" ? r.id : undefined;
      if (id) onUpdate((b) => ({ ...b, responseId: id }));
      return;
    }
    case "response.output_text.delta": {
      const delta = typeof data.delta === "string" ? data.delta : "";
      if (!delta) return;
      onUpdate((b) => ({
        ...b,
        text: b.text + delta,
        // First text delta closes out the thinking stream — useful when
        // the model emits all reasoning before any visible output.
        thinkingActive: false,
      }));
      return;
    }
    case "response.output_text.done":
      // Final text already accumulated via deltas — nothing to do.
      return;
    case "response.reasoning_text.delta": {
      const delta = typeof data.delta === "string" ? data.delta : "";
      if (!delta) return;
      onUpdate((b) => ({
        ...b,
        thinking: b.thinking + delta,
        thinkingActive: true,
      }));
      return;
    }
    case "response.reasoning_text.done": {
      // The aggregated text is in `data.text`; if we've been accumulating
      // deltas we already have it, but fall back to the final value if
      // the bubble somehow missed deltas (e.g. resumed mid-stream).
      const finalText = typeof data.text === "string" ? data.text : "";
      onUpdate((b) => ({
        ...b,
        thinking: b.thinking || finalText,
        thinkingActive: false,
      }));
      return;
    }
    case "response.output_item.added":
    case "response.output_item.done": {
      const item = data.item as Record<string, unknown> | undefined;
      if (item?.type === "function_call") {
        const id =
          (typeof item.call_id === "string" && item.call_id) ||
          (typeof item.id === "string" && item.id) ||
          undefined;
        if (!id) return;
        const args = parseMaybeJson(item.arguments);
        const status: ToolCallEntry["status"] =
          item.status === "failed"
            ? "failed"
            : item.status === "in_progress"
              ? "running"
              : t === "response.output_item.done"
                ? "succeeded"
                : "requested";
        upsertToolCall(onUpdate, id, (prev) => ({
          ...prev,
          toolId: typeof item.name === "string" ? item.name : prev.toolId,
          status,
          arguments: args ?? prev.arguments,
          startedAt: prev.startedAt ?? Date.now(),
        }));
      }
      return;
    }
    case "response.completed": {
      const r = data.response as Record<string, unknown> | undefined;
      const usage = r?.usage as Record<string, unknown> | undefined;
      if (usage) {
        const inputTokens = numOr(usage.input_tokens, 0);
        const outputTokens = numOr(usage.output_tokens, 0);
        const totalTokens = numOr(usage.total_tokens, inputTokens + outputTokens);
        onUpdate((b) => ({
          ...b,
          usage: { inputTokens, outputTokens, totalTokens },
          thinkingActive: false,
          reaction: undefined,
        }));
      } else {
        onUpdate((b) => ({ ...b, thinkingActive: false, reaction: undefined }));
      }
      return;
    }
    case "response.failed": {
      const r = data.response as Record<string, unknown> | undefined;
      const error = r?.error as { message?: string } | undefined;
      const message = error?.message ?? "(no detail)";
      onUpdate((b) => ({
        ...b,
        events: [...b.events, { id: noteId(), text: `[failed] ${message}`, kind: "error" }],
        errored: true,
        thinkingActive: false,
        reaction: undefined,
      }));
      return;
    }
    default:
      return;
  }
}

function upsertToolCall(
  onUpdate: (patch: (prev: BubbleEvent) => BubbleEvent) => void,
  id: string,
  apply: (prev: ToolCallEntry) => ToolCallEntry,
): void {
  onUpdate((b) => {
    const list = b.toolCalls;
    const idx = list.findIndex((e) => e.id === id);
    if (idx === -1) {
      const created = apply({ id, toolId: id, status: "requested" });
      return { ...b, toolCalls: [...list, created] };
    }
    const existing = list[idx];
    if (!existing) return b;
    const next = [...list];
    next[idx] = apply(existing);
    return { ...b, toolCalls: next };
  });
}

function parseMaybeJson(value: unknown): Record<string, unknown> | undefined {
  if (typeof value === "object" && value !== null) return value as Record<string, unknown>;
  if (typeof value !== "string" || value.length === 0) return undefined;
  try {
    const parsed = JSON.parse(value);
    return typeof parsed === "object" && parsed !== null
      ? (parsed as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
}

function numOr(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}
